import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { RentalSettlementPreview, RentalSettlementRun } from '../rental.model';
import { RentalService } from '../rental.service';

@Component({
    selector: 'app-rental-settlements',
    standalone: true,
    templateUrl: './rental-settlements.component.html',
    styleUrls: ['./rental-settlements.component.css'],
    imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, DialogModule, IftaLabelModule, InputGroupAddonModule, InputGroupModule, InputNumberModule, InputTextModule, ProgressBarModule, TableModule, TagModule, TextareaModule, ToastModule, PageShellComponent, PageHeroComponent, PageDataPanelComponent],
    providers: [MessageService]
})
export class RentalSettlementsComponent implements OnInit {
    fromDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    toDate = new Date();
    preview: RentalSettlementPreview[] = [];
    runs: RentalSettlementRun[] = [];
    loading = false;
    saving = false;
    partialDialogVisible = false;
    invoiceDialogVisible = false;
    disputeDialogVisible = false;
    partialUsageIds = '';
    selectedRun?: RentalSettlementRun;
    disputeAction = '';
    disputeNotes = '';
    invoiceDraft = { vendorInvoiceNumber: '', vendorInvoiceAmount: null as number | null, notes: '' };
    readonly canGenerate: boolean;
    readonly canManage: boolean;
    readonly canViewFinance: boolean;

    constructor(private rentalService: RentalService, private session: SessionStorageService, private permissions: PermissionService, private router: Router, private messages: MessageService) {
        this.canGenerate = permissions.hasPermission(app_permissions.fleet.rental.settlement.generate);
        this.canManage = permissions.hasPermission(app_permissions.fleet.rental.settlement.manage);
        this.canViewFinance = permissions.hasPermission(app_permissions.fleet.rental.financeView);
    }

    ngOnInit(): void { this.load(); }

    load(): void {
        const companyId = this.companyId();
        if (!companyId || !this.fromDate || !this.toDate) return;
        this.loading = true;
        this.rentalService.previewSettlements(companyId, this.isoDate(this.fromDate)!, this.isoDate(this.toDate)!).subscribe({ next: rows => { this.preview = rows; this.loading = false; }, error: error => { this.preview = []; this.loading = false; this.error(error?.error?.message || 'Could not load the settlement preview.'); } });
        this.rentalService.searchSettlements(companyId).subscribe({ next: rows => this.runs = rows, error: () => this.runs = [] });
    }

    generate(): void {
        const companyId = this.companyId();
        if (!companyId || !this.fromDate || !this.toDate) return;
        this.saving = true;
        this.rentalService.generateSettlements(companyId, this.isoDate(this.fromDate)!, this.isoDate(this.toDate)!).subscribe({ next: rows => { this.saving = false; this.runs = rows; this.success(`${rows.length} settlement run(s) generated.`); this.load(); }, error: error => { this.saving = false; this.error(error?.error?.message || 'Could not generate rental settlements.'); } });
    }

    openPartialDialog(): void { this.partialUsageIds = ''; this.partialDialogVisible = true; }
    generatePartial(): void {
        const companyId = this.companyId();
        const usageDayIds = [...new Set(this.partialUsageIds.split(',').map(value => Number(value.trim())).filter(value => Number.isInteger(value) && value > 0))];
        if (!companyId || !usageDayIds.length || !this.fromDate || !this.toDate) { this.error('Enter one or more valid approved usage-day IDs.'); return; }
        this.saving = true;
        this.rentalService.generatePartialSettlements(companyId, this.isoDate(this.fromDate)!, this.isoDate(this.toDate)!, usageDayIds).subscribe({ next: rows => { this.saving = false; this.partialDialogVisible = false; this.success(`${rows.length} partial settlement run(s) generated.`); this.load(); }, error: error => { this.saving = false; this.error(error?.error?.message || 'Could not generate the partial settlement.'); } });
    }

    retry(run: RentalSettlementRun): void {
        const companyId = this.companyId();
        if (!companyId || !run.rentalSettlementRunId) return;
        this.saving = true;
        this.rentalService.retrySettlement(run.rentalSettlementRunId, companyId).subscribe({ next: () => { this.saving = false; this.success('Settlement handoff retried.'); this.load(); }, error: error => { this.saving = false; this.error(error?.error?.message || 'Could not retry the AP handoff.'); } });
    }

    createBill(run: RentalSettlementRun): void {
        const companyId = this.companyId();
        if (!companyId || !run.rentalSettlementRunId) return;
        this.saving = true;
        this.rentalService.generateSettlementBill(run.rentalSettlementRunId, companyId).subscribe({ next: () => { this.saving = false; this.success('AP bill draft created.'); this.load(); }, error: error => { this.saving = false; this.error(error?.error?.message || 'Could not create the AP bill draft.'); } });
    }

    openInvoiceDialog(run: RentalSettlementRun): void { this.selectedRun = run; this.invoiceDraft = { vendorInvoiceNumber: run.vendorInvoiceNumber || '', vendorInvoiceAmount: run.vendorInvoiceAmount ?? run.totalAmount ?? null, notes: run.notes || '' }; this.invoiceDialogVisible = true; }
    recordInvoice(): void {
        const companyId = this.companyId();
        if (!companyId || !this.selectedRun?.rentalSettlementRunId || !this.invoiceDraft.vendorInvoiceNumber || this.invoiceDraft.vendorInvoiceAmount == null) { this.error('Vendor invoice number and amount are required.'); return; }
        this.saving = true;
        this.rentalService.recordSettlementInvoice(this.selectedRun.rentalSettlementRunId, companyId, { vendorInvoiceNumber: this.invoiceDraft.vendorInvoiceNumber, vendorInvoiceAmount: this.invoiceDraft.vendorInvoiceAmount, notes: this.invoiceDraft.notes }).subscribe({ next: () => { this.saving = false; this.invoiceDialogVisible = false; this.success('Vendor invoice and variance recorded.'); this.load(); }, error: error => { this.saving = false; this.error(error?.error?.message || 'Could not record the vendor invoice.'); } });
    }

    openDisputeDialog(run: RentalSettlementRun, action: string): void { this.selectedRun = run; this.disputeAction = action; this.disputeNotes = ''; this.disputeDialogVisible = true; }
    changeDispute(): void {
        const companyId = this.companyId();
        if (!companyId || !this.selectedRun?.rentalSettlementRunId) return;
        this.saving = true;
        this.rentalService.changeSettlementDispute(this.selectedRun.rentalSettlementRunId, companyId, this.disputeAction, this.disputeNotes).subscribe({ next: () => { this.saving = false; this.disputeDialogVisible = false; this.success('Settlement dispute updated.'); this.load(); }, error: error => { this.saving = false; this.error(error?.error?.message || 'Could not update the settlement dispute.'); } });
    }

    viewBill(run: RentalSettlementRun): void { if (run.billId) this.router.navigate(['/accounts/enterprise/payables/generated-bills/view', run.billId]); }
    runSeverity(run: RentalSettlementRun): 'success' | 'warning' | 'danger' | 'info' { return run.status === 'PAYABLE_CREATED' || run.status === 'BILL_DRAFT_CREATED' ? 'success' : run.status === 'FAILED' ? 'danger' : 'warning'; }
    private companyId(): number { return this.session.getCompany()?.id ?? 0; }
    private isoDate(value?: Date | null): string | null { return value ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}` : null; }
    private success(detail: string): void { this.messages.add({ severity: 'success', summary: 'Rental settlements', detail }); }
    private error(detail: string): void { this.messages.add({ severity: 'error', summary: 'Rental settlements', detail }); }
}
